import React, { useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  ListItem,
  Tooltip,
  Avatar,
  Menu,
  MenuItem,
} from "@mui/material";
import {
  Home,
  Folder,
  Category,
  Star,
  Person,
  Settings,
  HelpOutline,
  Notifications,
} from "@mui/icons-material";
import { styled } from "@mui/system";
import LogoImage from "../../Screenshot_4.png";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import StorefrontIcon from "@mui/icons-material/Storefront";
import CalculateIcon from "@mui/icons-material/Calculate";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import SellIcon from "@mui/icons-material/Sell";
import LogoutIcon from "@mui/icons-material/Logout";
import ExploreIcon from "@mui/icons-material/Explore";
import DiamondOutlinedIcon from "@mui/icons-material/DiamondOutlined";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { persistor } from "../../redux/store";
import { useAppTheme } from "../../theme/useAppTheme";

const SidebarContainer = styled(Box)({
  width: "90px",
  height: "100vh",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  borderRight: "1px solid #e0e0e0",
  position: "fixed",
  top: 0,
  left: 0,
  zIndex: 1200,
  background: "#ffffff",
  "@media (max-width: 899.95px)": {
    display: "none",
  },
});

const ScrollableList = styled(Box)({
  flex: 1,
  overflowY: "auto",
  width: "100%",
  paddingTop: "20px",
  scrollbarWidth: "thin",
  "&::-webkit-scrollbar": {
    width: "6px",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "#c0b3e0",
    borderRadius: "10px",
  },
});

const BottomIcons = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "15px",
  paddingBottom: "15px",
  borderTop: "1px solid #e0e0e0",
  paddingTop: "10px",
});

const Sidebar = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const [moreAnchor, setMoreAnchor] = useState(null); 
  const { customer: fetchedCustomer } = useSelector(
    (state) => state.profile || {},
  );
  const { appConfig } = useSelector((state) => state.dashboard || {});
  const artificialJewelleryEnabled =
    appConfig?.featureToggle?.Artificial_Jewellery === true;
  const customer = fetchedCustomer || null;
  const initials = customer
    ? `${customer.firstName?.trim()[0] || ""}${
        customer.lastName?.trim()[0] || ""
      }`.toUpperCase()
    : "";

  const handleLogout = async () => {
    const confirmLogout = window.confirm("Are you sure you want to logout?");
    if (!confirmLogout) return;

    localStorage.clear();
    sessionStorage.clear();

    dispatch({ type: "RESET_APP" });

    if (persistor) {
      await persistor.purge();
    }

    window.location.replace("/login");
  };

  const themeColors = useAppTheme();
  const sidebarItems = [
    { icon: <Home />, label: "Home", path: "/Dashboard" },
    { icon: <BusinessCenterIcon />, label: "Saving", path: "/SavingsScreen" },
    { icon: <StorefrontIcon />, label: "Store", path: "/productView" },
    {
      icon: <DiamondOutlinedIcon />,
      label: "Artificial Jewellery",
      path: "/artificialJewellery",
      matchPrefix: "/artificialJewellery",
    },
    { icon: <ExploreIcon />, label: "Explore", path: "/explore" },
  ].filter(
    (item) =>
      item.path !== "/artificialJewellery" || artificialJewelleryEnabled,
  );

  return (
    <>
    <SidebarContainer sx={{ background: themeColors.bottomNavBg }}>
      <Box sx={{ mt: 2, mb: 1 }}>
        <img
          src={LogoImage}
          alt="App Logo"
          style={{
            width: "50px",
            height: "50px",
            borderRadius: "12px",
            objectFit: "contain",
            background: themeColors.bottomNavBg,
          }}
        />
      </Box>

      <ScrollableList>
        {sidebarItems.map((item, index) => {
          const isSelected = location.pathname === item.path;

          return (
            <Tooltip key={index} title={item.label} placement="right">
              <ListItem
                button
                component={Link}
                to={item.path}
                sx={{
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 1,
                  borderRadius: "12px",

                  background: isSelected
                    ? themeColors.mediumContainer
                    : themeColors.bottomNavBg,

                  /* hover */
                  "&:hover": {
                    background: themeColors.mediumContainer,
                  },
                }}
              >
                <IconButton
                  sx={{
                    color: isSelected
                      ? themeColors.primary 
                      : themeColors.text,
                  }}
                >
                  {item.icon}
                </IconButton>

                <Typography
                  variant="caption"
                  sx={{
                    fontSize: "11px",
                    fontWeight: 600,
                    textAlign: "center",
                    color: isSelected
                      ? themeColors.primary 
                      : themeColors.text,
                  }}
                >
                  {item.label}
                </Typography>
              </ListItem>
            </Tooltip>
          );
        })}
      </ScrollableList>

      <BottomIcons>
        <Tooltip title="Notifications" placement="right">
          <IconButton
            sx={{
              background: themeColors.primary,
              border: `1px solid ${themeColors.border}`,
              color: themeColors.text,
              "&:hover": { color: themeColors.bottomNavSelected },
            }}
            onClick={() => navigate("/Notification")}
          >
            <Notifications />
          </IconButton>
        </Tooltip>

        <Tooltip title="Profile" placement="right">
          <Avatar
            sx={{
              width: 38,
              height: 38,
              cursor: "pointer",
              background: themeColors.primary,
              border: `1px solid ${themeColors.border}`,
              color: themeColors.text,
              fontWeight: 600,
              fontSize: "16px",
              "&:hover": { color: "#9c27b0" },
            }}
            onClick={() => navigate("/Content")}
          >
            {initials || "U"}
          </Avatar>
        </Tooltip>
        <Tooltip title="Logout" placement="right">
          <IconButton
            sx={{
              background: themeColors.primary,
              border: `1px solid ${themeColors.border}`,
              color: themeColors.text,
              mt: 1,
              "&:hover": { color: "#9c27b0" },
            }}
            onClick={handleLogout}
          >
            <LogoutIcon />
          </IconButton>
        </Tooltip>
      </BottomIcons>
    </SidebarContainer>

    <Box
      component="nav"
      aria-label="Primary"
      sx={{
        display: "none",
        "@media (max-width: 899.95px)": {
          display: "flex",
        },
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1300,
        background: themeColors.bottomNavBg,
        borderTop: `1px solid ${themeColors.border}`,
        justifyContent: "space-around",
        alignItems: "stretch",
        px: 0.5,
        pt: 0.5,
        pb: "calc(4px + env(safe-area-inset-bottom))",
        boxShadow: "0 -6px 18px rgba(0,0,0,0.08)",
      }}
    >
      {sidebarItems.map((item) => {
        const isSelected = location.pathname === item.path;

        return (
          <Box
            key={item.path}
            component={Link}
            to={item.path}
            sx={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              textDecoration: "none",
              py: 0.5,
              borderRadius: "12px",
              color: isSelected
                ? themeColors.bottomNavSelected
                : themeColors.text,
              background: isSelected
                ? themeColors.mediumContainer
                : "transparent",
            }}
          >
            {item.icon}
            <Typography
              sx={{
                fontSize: "10px",
                fontWeight: 600,
                lineHeight: 1.2,
                mt: 0.25,
                textAlign: "center",
              }}
            >
              {item.label}
            </Typography>
          </Box>
        );
      })}

      <Box
        component="button"
        type="button"
        aria-label="More"
        onClick={(event) => setMoreAnchor(event.currentTarget)}
        sx={{
          flex: 1,
          minWidth: 0,
          border: 0,
          background:
            location.pathname === "/Notification" ||
            location.pathname === "/Content" ||
            location.pathname === "/Profile"
              ? themeColors.mediumContainer
              : "transparent",
          color: themeColors.text,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          py: 0.5,
          borderRadius: "12px",
          cursor: "pointer",
        }}
      >
        <MoreHorizIcon />
        <Typography
          sx={{
            fontSize: "10px",
            fontWeight: 600,
            lineHeight: 1.2,
            mt: 0.25,
          }}
        >
          More
        </Typography>
      </Box>

      <Menu
        anchorEl={moreAnchor}
        open={Boolean(moreAnchor)}
        onClose={() => setMoreAnchor(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <MenuItem
          onClick={() => {
            setMoreAnchor(null);
            navigate("/Notification");
          }}
        >
          <Notifications fontSize="small" sx={{ mr: 1 }} />
          Notifications
        </MenuItem>
        <MenuItem
          onClick={() => {
            setMoreAnchor(null);
            navigate("/Content");
          }}
        >
          <Person fontSize="small" sx={{ mr: 1 }} />
          Profile
        </MenuItem>
        <MenuItem
          onClick={() => {
            setMoreAnchor(null);
            handleLogout();
          }}
        >
          <LogoutIcon fontSize="small" sx={{ mr: 1 }} />
          Logout
        </MenuItem>
      </Menu>
    </Box>
    </>
  );
};

export default Sidebar;
